import { useMemo } from 'react';
import { Object3D } from 'three';
import { ObjectMap } from '@react-three/fiber';
import { useLoaderWithSignedCache, LoaderProto, LoaderReturnType, BranchingReturn } from './use-loader-with-signed-cache';

type GLTFLike = { scene: Object3D };

// One clone per (instanceKey, raw model). WeakMap keyed on the raw Object3D so
// the clone is eligible for GC if suspend-react ever releases the raw model.
// instanceKey distinguishes components that may share the same modelPath (e.g.
// subject + subjectContext), ensuring each gets its own independent Object3D.
const cloneCache = new Map<string, WeakMap<Object3D, Object3D>>();

function getOrCreateClone(instanceKey: string, raw: Object3D): Object3D {
  let instanceMap = cloneCache.get(instanceKey);
  if (!instanceMap) {
    instanceMap = new WeakMap();
    cloneCache.set(instanceKey, instanceMap);
  }
  let clone = instanceMap.get(raw);
  if (!clone) {
    clone = raw.clone();
    instanceMap.set(raw, clone);
  }
  return clone;
}

/**
 * Loads a 3D model via `useLoaderWithSignedCache` and returns a stable cloned instance.
 *
 * `instanceKey` must be unique per usage site (e.g. `'subject'`, `'subjectContext'`).
 * It ensures components that share a modelPath each get their own independent Object3D,
 * preventing material mutation and scene-graph conflicts.
 *
 * Clones are cached at module level so navigating back to a previously-visited subject
 * reuses the existing clone and its GPU-uploaded materials, avoiding re-upload cost.
 *
 * `useMemo` is explicit here even with React Compiler: the compiler cannot prove that
 * the cache-lookup is pure, so without it `clone` would be a new reference on every
 * render, causing R3F to thrash the portal scene graph on every frame.
 */
export function useModel<T, L extends LoaderProto<T>, R = LoaderReturnType<T, L>>(
  Proto: L,
  modelPath: string,
  instanceKey: string,
  onProgress?: (event: ProgressEvent<EventTarget>) => void
): BranchingReturn<R, GLTFLike, R & ObjectMap> {
  const raw = useLoaderWithSignedCache(Proto, modelPath, undefined, onProgress);
  const clone = useMemo(
    () => getOrCreateClone(instanceKey, raw as unknown as Object3D),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [raw]
  );
  // Double cast needed: TypeScript cannot resolve the nested conditional types at the generic
  // level, but the declared return type is correct for all callers.
  return clone as unknown as BranchingReturn<R, GLTFLike, R & ObjectMap>;
}
